import { useEffect, useMemo, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import {
  LayoutResponseType,
  LayoutRowType,
  SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT,
  hideLoader,
  showLoader,
} from '@app-store'
import { MODE_CONSTANTS } from '@ui-kit'
import { isEmpty } from 'lodash'
import SubFormRenderer from '../SubFormRenderer'
import { EntityLayout } from '../../types/layout.types'
import { RowTabIndex } from '../../types/common'
import { componentNames } from '../../utils/constants'
import { formatDataBeforeLoad } from '../../utils/form.utils'
import { getEntityDataByIdEmbedded } from '../../apis/entityData.api'
import { useAppDispatch } from '../../utils/agent.store'
import { FormAncestryScope } from '../../form-behavior/FormAncestryScope'
import { isRowHiddenByFormBehavior } from '../../form-behavior/formBehavior.utils'
import { useFormBehaviorState } from '../../form-behavior/useFormBehaviorState'

export interface TabSubFormRowProps {
  row: LayoutRowType
  layout: LayoutResponseType
  data: FieldValues
  index?: number
  tabIndexMap?: RowTabIndex
  isRootEntityTab?: boolean
  addProductIdFilterForPaysign?: boolean
  layoutRowType?: string
}

const TabSubFormRow = ({
  row,
  layout,
  data,
  tabIndexMap,
  isRootEntityTab = false,
  addProductIdFilterForPaysign = false,
  layoutRowType,
}: TabSubFormRowProps) => {
  const dispatch = useAppDispatch()
  const [, forceUpdate] = useState(0)
  const methods = useForm({ reValidateMode: 'onBlur' })
  const { reset, setValue, getValues } = methods
  const mode = MODE_CONSTANTS.VIEW
  const formBehavior = useFormBehaviorState(layout?.uid)

  const formUniqueId = useMemo(
    () => `tab-sub-form-${row?.rowKey || row?.rowId || uuid()}`,
    [row?.rowId, row?.rowKey]
  )
  const reregisterKey = row?.rowKey || row?.rowId || formUniqueId

  const addedFields = layout?.layout_data?.added_fields ?? {}

  useEffect(() => {
    if (!data || isEmpty(data)) return
    formatDataBeforeLoad(data, addedFields)
    reset(data)
  }, [addedFields, data, reset])

  useEffect(() => {
    if (isEmpty(layout) || isEmpty(data)) return

    const singleLookupFields = Object.keys(addedFields).filter(
      (key) => addedFields[key]?.ui_data_type === componentNames.SINGLE_LOOKUP
    )

    if (!singleLookupFields.length) return

    dispatch(showLoader())
    const lookupRequests = singleLookupFields
      .map((fieldName) => {
        const lookupAddedFields = addedFields?.[fieldName]?.added_fields ?? {}
        if (isEmpty(lookupAddedFields)) return null
        if (
          !addedFields?.[fieldName]?.relationship?.target_entity ||
          !data?.[fieldName]?.id
        ) {
          return null
        }

        return getEntityDataByIdEmbedded(
          addedFields[fieldName]?.relationship?.target_entity ?? '',
          Number(addedFields[fieldName]?.relationship?.target_entity_id),
          Object.keys(lookupAddedFields),
          data[fieldName]?.id
        )
          .then((response) => {
            setValue(fieldName, response?.data)
          })
          .catch((error) => {
            console.error('Error fetching sub-form lookup data:', error)
          })
      })
      .filter(Boolean)

    Promise.all(lookupRequests).finally(() => {
      forceUpdate((prev) => prev + 1)
      dispatch(hideLoader())
    })
  }, [addedFields, data, dispatch, layout, setValue])

  if (isRowHiddenByFormBehavior(row?.rowKey, formBehavior.hiddenOverrides)) {
    return null
  }

  const subFormContent = (
    <div id={row?.rowKey || row?.rowId}>
      <SubFormRenderer
        key={row?.rowId}
        row={row}
        layout={layout as EntityLayout}
        mode={mode}
        methods={methods}
        isMultiLookup={row?.lookup_meta?.type === componentNames.MULTI_LOOKUP}
        defaultExpandedCount={
          row?.add_button_behavior ===
          SUBFORM_ADD_BUTTON_BEHAVIOR_CONSTANT.DISABLED_ADD
            ? row?.initial_count
            : 1
        }
        maxExpandedCount={row?.initial_count}
        reregisterKey={reregisterKey}
        formUniqueId={formUniqueId}
        depth={0}
        tabOffset={tabIndexMap?.tabIndexOffset}
        addProductIdFilterForPaysign={addProductIdFilterForPaysign}
        layoutRowType={layoutRowType}
      />
    </div>
  )

  return (
    <FormProvider {...methods}>
      {layout?.entity_name ? (
        <FormAncestryScope
          entityName={layout.entity_name}
          layoutUid={layout.uid}
          getValues={getValues}
          isRootInstance={isRootEntityTab}
        >
          {subFormContent}
        </FormAncestryScope>
      ) : (
        subFormContent
      )}
    </FormProvider>
  )
}

export default TabSubFormRow
